import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Search, Plus, Users, ChevronRight, Loader2 } from 'lucide-react';
import EmptyState from '@/components/EmptyState';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };
const fmt$ = v => { if (!v) return '$0'; if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`; if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`; return `$${Math.round(v).toLocaleString()}`; };

export default function BrokerClientsPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [clients, setClients] = useState(null);
  const [q, setQ] = useState('');
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/broker/clients?q=${encodeURIComponent(q)}`)
      .then(r => setClients(r.data.clients))
      .catch(() => setClients([]));
  }, [api, q]);

  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  return (
    <div className="space-y-4" data-testid="broker-clients-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: C.navy }}>Clients</h2>
          <p className="text-xs mt-0.5" style={{ color: C.slate }}>Every insured and prospect on your book</p>
        </div>
        <button onClick={() => setAddOpen(true)} data-testid="add-client-btn"
          className="flex items-center gap-1.5 px-3.5 py-2 rounded-md text-xs font-semibold text-white transition-opacity hover:opacity-90"
          style={{ background: '#FF9933' }}>
          <Plus className="w-3.5 h-3.5" />Add Client
        </button>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: C.slate }} />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search clients by name or ABN..."
          data-testid="client-search-input"
          className="w-full pl-9 pr-3 py-2 text-sm rounded-md border bg-white focus:outline-none focus:ring-1"
          style={{ borderColor: 'rgba(136,150,168,0.25)', color: C.navy }} />
      </div>

      {clients === null ? (
        <div className="space-y-2">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-14 rounded-md" />)}</div>
      ) : clients.length === 0 ? (
        q ? (
          <Card className="p-10 text-center rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
            <Users className="w-8 h-8 mx-auto mb-2" style={{ color: C.slate }} />
            <p className="text-sm" style={{ color: C.slate }}>No clients match your search.</p>
          </Card>
        ) : (
          <Card className="rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
            <EmptyState type="clients" compact />
          </Card>
        )
      ) : (
        <Card className="rounded-md overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="client-list">
          <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2 text-[9px] uppercase tracking-wider font-semibold border-b"
            style={{ color: C.slate, borderColor: 'rgba(136,150,168,0.12)', background: '#FAFBFC' }}>
            <span className="col-span-4">Client</span>
            <span className="col-span-2 text-right">GWP</span>
            <span className="col-span-1 text-center">Policies</span>
            <span className="col-span-2 text-center">Pending Quotes</span>
            <span className="col-span-1 text-center">Claims</span>
            <span className="col-span-2 text-center">Renewing 60d</span>
          </div>
          {clients.map(c => (
            <button key={c.entity_id} onClick={() => navigate(`/clients/${c.entity_id}`)}
              data-testid={`client-row-${c.entity_id}`}
              className="w-full grid grid-cols-12 gap-2 items-center px-4 py-2.5 border-b text-left transition-colors hover:bg-blue-50/40"
              style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
              <div className="col-span-8 md:col-span-4 min-w-0 flex items-center gap-2">
                <div className="min-w-0">
                  <p className="text-xs font-medium truncate" style={{ color: C.navy }}>{c.name || c.entity_id}</p>
                  <p className="text-[10px]" style={{ color: C.slate }}>{c.abn ? `ABN ${c.abn}` : ''}</p>
                </div>
                {c.prospect && <span className="px-1.5 py-px rounded text-[8px] font-bold shrink-0" style={{ background: 'rgba(0,180,216,0.12)', color: '#0E7490' }}>PROSPECT</span>}
              </div>
              <span className="hidden md:block col-span-2 text-right text-xs font-mono" style={{ color: C.navy }}>{fmt$(c.gwp)}</span>
              <span className="hidden md:block col-span-1 text-center text-xs font-mono" style={{ color: C.blue }}>{c.active_policies}</span>
              <span className="hidden md:block col-span-2 text-center text-xs font-mono" style={{ color: c.pending_quotes > 0 ? '#B27516' : C.slate }}>{c.pending_quotes}</span>
              <span className="hidden md:block col-span-1 text-center text-xs font-mono" style={{ color: c.open_claims > 0 ? C.red : C.slate }}>{c.open_claims}</span>
              <div className="col-span-4 md:col-span-2 flex items-center justify-end md:justify-center gap-1">
                <span className="text-xs font-mono" style={{ color: c.expiring_60d > 0 ? '#B27516' : C.slate }}>{c.expiring_60d}</span>
                <ChevronRight className="w-3.5 h-3.5 md:hidden" style={{ color: C.slate }} />
              </div>
            </button>
          ))}
        </Card>
      )}

      <AddClientDialog open={addOpen} onClose={() => setAddOpen(false)} onAdded={eid => { setAddOpen(false); navigate(`/clients/${eid}`); }} />
    </div>
  );
}

function AddClientDialog({ open, onClose, onAdded }) {
  const api = useApi();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState('');

  useEffect(() => {
    if (!open) { setQ(''); setResults([]); return; }
  }, [open]);

  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(() => {
      api.get(`/entities/search?q=${encodeURIComponent(q)}&limit=8`)
        .then(r => setResults(r.data))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q, api]);

  const add = async (e) => {
    setAdding(e.entity_id);
    try {
      await api.post('/broker/clients', { entity_id: e.entity_id });
      toast.success(`${e.legal_name} added to your clients`);
      onAdded(e.entity_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add client');
    }
    setAdding('');
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="add-client-dialog">
        <DialogHeader>
          <DialogTitle className="text-base" style={{ color: C.navy }}>Add a Client</DialogTitle>
          <DialogDescription className="text-xs" style={{ color: C.slate }}>
            Search the entity register for the business you want to quote or manage.
          </DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: C.slate }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Business name or ABN..." autoFocus
            data-testid="add-client-search-input"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-md border focus:outline-none focus:ring-1"
            style={{ borderColor: 'rgba(136,150,168,0.25)', color: C.navy }} />
        </div>
        <div className="max-h-64 overflow-y-auto space-y-1">
          {searching && <p className="text-xs py-2 text-center" style={{ color: C.slate }}>Searching…</p>}
          {!searching && q.length >= 2 && results.length === 0 && <p className="text-xs py-2 text-center" style={{ color: C.slate }}>No entities found.</p>}
          {results.map(e => (
            <button key={e.entity_id} onClick={() => add(e)} disabled={!!adding}
              data-testid={`add-client-result-${e.entity_id}`}
              className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-md border text-left transition-colors hover:bg-blue-50/50 disabled:opacity-60"
              style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
              <div className="min-w-0">
                <p className="text-xs font-medium truncate" style={{ color: C.navy }}>{e.legal_name}</p>
                <p className="text-[10px]" style={{ color: C.slate }}>ABN {e.abn} · {e.suburb || ''} {e.state || ''}</p>
              </div>
              {adding === e.entity_id ? <Loader2 className="w-4 h-4 animate-spin shrink-0" style={{ color: C.blue }} /> : <Plus className="w-4 h-4 shrink-0" style={{ color: C.green }} />}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
